/**
 * `usePreferencesStore` — Zustand slice over the End_User's `Preferences`.
 *
 * Holds language, practice mode, active theme id, pacing intensity,
 * reduced-motion override, token budget, and the dev-only forced model
 * (Requirements 3.3, 3.4, 7.5, 8.7, 13.1, 13.2, 13.4). Patches persist
 * immediately through the active `ConversationStore` so subsequent
 * prompt builds and theme applies see the new value without a reload
 * (Requirement 13.2).
 *
 * On `hydrate` the slice loads the persisted preferences. When none
 * exist (first boot or after `wipeAll`), the slice seeds the defaults
 * from `defaultPreferences()` and writes them back so the singleton is
 * always present in subsequent reads.
 *
 * The convenience setters are thin wrappers around `patch` so callers
 * can write `setLanguage('en')` instead of `patch({ language: 'en' })`.
 * They exist purely for ergonomics — the underlying `patch` is the
 * only persistence path.
 */

import { create } from 'zustand';

import type { ModelId } from '../domain/modelRouter/types';
import { defaultPreferences } from '../domain/types';
import type {
  LanguageCode,
  PacingIntensity,
  PracticeMode,
  Preferences,
  ReducedMotionOverride,
  VoiceName,
} from '../domain/types';

import { getActiveConversationStore } from './storeContext';

/** Public slice surface. */
export interface PreferencesState {
  /** Current `Preferences`. Always defined; never `null` after hydrate. */
  preferences: Preferences;
  /** `true` once `hydrate()` has resolved. */
  hydrated: boolean;
  /** Read the persisted preferences (or seed defaults). */
  hydrate(): Promise<void>;
  /** Apply a partial patch and persist atomically. */
  patch(partial: Partial<Preferences>): Promise<void>;
  /** Convenience setter — language. */
  setLanguage(language: LanguageCode): Promise<void>;
  /** Convenience setter — language-practice mode. */
  setPracticeMode(mode: PracticeMode): Promise<void>;
  /** Convenience setter — active theme id. */
  setActiveThemeId(id: string): Promise<void>;
  /** Convenience setter — Compliment_Pacing_Policy intensity. */
  setPacing(pacing: PacingIntensity): Promise<void>;
  /** Convenience setter — reduced-motion override. */
  setReducedMotionOverride(value: ReducedMotionOverride): Promise<void>;
  /** Convenience setter — token budget for prompt history. */
  setTokenBudget(tokens: number): Promise<void>;
  /** Convenience setter — dev forced model. */
  setForcedModel(modelId: ModelId | null): Promise<void>;
  /** Convenience setter — voice for sesli konuşma. */
  setVoiceName(voice: VoiceName): Promise<void>;
}

export const usePreferencesStore = create<PreferencesState>((set, get) => ({
  preferences: defaultPreferences(),
  hydrated: false,

  async hydrate(): Promise<void> {
    const store = getActiveConversationStore();
    const persisted = await store.getPreferences();
    if (persisted !== null) {
      // Merge with defaults so newly added preference fields (e.g.
      // `voiceName`) do not surface as `undefined` for users who
      // hydrated before the field existed.
      const merged: Preferences = { ...defaultPreferences(), ...persisted };
      set({ preferences: merged, hydrated: true });
      return;
    }
    const seeded = defaultPreferences();
    set({ preferences: seeded, hydrated: true });
    await store.setPreferences(seeded);
  },

  async patch(partial: Partial<Preferences>): Promise<void> {
    const next: Preferences = { ...get().preferences, ...partial };
    set({ preferences: next });
    await getActiveConversationStore().setPreferences(next);
  },

  // Convenience setters — all delegate to `patch` so persistence and
  // change-notification flow through a single path.
  setLanguage(language): Promise<void> {
    return get().patch({ language });
  },
  setPracticeMode(practiceMode): Promise<void> {
    return get().patch({ practiceMode });
  },
  setActiveThemeId(activeThemeId): Promise<void> {
    return get().patch({ activeThemeId });
  },
  setPacing(pacing): Promise<void> {
    return get().patch({ pacing });
  },
  setReducedMotionOverride(reducedMotionOverride): Promise<void> {
    return get().patch({ reducedMotionOverride });
  },
  setTokenBudget(tokenBudget): Promise<void> {
    return get().patch({ tokenBudget });
  },
  setForcedModel(forcedModel): Promise<void> {
    return get().patch({ forcedModel });
  },
  setVoiceName(voiceName): Promise<void> {
    return get().patch({ voiceName });
  },
}));
